import React, { useState, useRef, useEffect } from 'react'
import { X, Play, Pause, RefreshCw } from 'lucide-react'
import { generateSpeech } from '../utils/speechSynthesis.js'
import toast from 'react-hot-toast'

function VoicePreview({ settings, onClose }) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [previewText, setPreviewText] = useState(
    'Hello! This is a preview of your current voice settings. You can adjust the accent, gender, speed, and pitch to customize how your text will sound when converted to speech.'
  )
  const [audioUrl, setAudioUrl] = useState(null)
  const audioRef = useRef(null)
  
  const sampleTexts = [
    'Hello! This is a preview of your current voice settings.',
    'The quick brown fox jumps over the lazy dog.',
    'Welcome to Voice Converter Pro. Your text-to-speech solution.',
    'Testing different voice settings helps you find the perfect sound.',
    'Numbers and dates: 123, January 1st, 2024, $99.99.'
  ]
  
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    
    const handleEnded = () => setIsPlaying(false)
    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)
    
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('play', handlePlay)
    audio.addEventListener('pause', handlePause)
    
    return () => {
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('play', handlePlay)
      audio.removeEventListener('pause', handlePause)
    }
  }, [])
  
  const handleGeneratePreview = async () => {
    if (!previewText.trim()) {
      toast.error('Please enter some text to preview')
      return
    }
    
    setIsLoading(true)
    
    try {
      const audioData = await generateSpeech(previewText, settings)
      setAudioUrl(audioData.audioUrl)
      
      // Auto-play the preview
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.src = audioData.audioUrl
          audioRef.current.play().catch(error => {
            console.error('Error playing preview:', error)
          })
        }
      }, 100)
      
      toast.success('Preview generated!')
    } catch (error) {
      toast.error('Failed to generate preview')
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }
  
  const handlePlayPause = () => {
    const audio = audioRef.current
    if (!audio || !audioUrl) {
      handleGeneratePreview()
      return
    }
    
    if (isPlaying) {
      audio.pause()
    } else {
      audio.play().catch(error => {
        console.error('Error playing audio:', error)
        toast.error('Failed to play audio')
      })
    }
  }
  
  const handleSampleTextSelect = (text) => {
    setPreviewText(text)
    setAudioUrl(null) // Clear previous audio
  }
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            Voice Preview
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Current Settings */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-3">Current Voice Settings</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-gray-600">Accent</div>
                <div className="font-medium">{settings.accent}</div>
              </div>
              <div>
                <div className="text-gray-600">Gender</div>
                <div className="font-medium capitalize">{settings.gender}</div>
              </div>
              <div>
                <div className="text-gray-600">Speed</div>
                <div className="font-medium">{settings.speed}x</div>
              </div>
              <div>
                <div className="text-gray-600">Pitch</div>
                <div className="font-medium">{settings.pitch}</div>
              </div>
            </div>
          </div>
          
          {/* Sample Texts */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Sample Texts</h3>
            <div className="grid grid-cols-1 gap-2">
              {sampleTexts.map((text, index) => (
                <button
                  key={index}
                  onClick={() => handleSampleTextSelect(text)}
                  className={`text-left p-3 rounded-lg border transition-colors ${
                    previewText === text
                      ? 'border-primary-300 bg-primary-50 text-primary-700'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="text-sm">{text}</div>
                </button>
              ))}
            </div>
          </div>
          
          {/* Custom Text Input */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Custom Preview Text</h3>
            <textarea
              value={previewText}
              onChange={(e) => setPreviewText(e.target.value)}
              placeholder="Enter your own text to preview..."
              className="input-field min-h-[100px] resize-y"
              maxLength={500}
            />
            <div className="mt-2 text-sm text-gray-500">
              {previewText.length}/500 characters
            </div>
          </div>
          
          {/* Audio Player */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <audio ref={audioRef} />
            
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={handlePlayPause}
                disabled={isLoading}
                className="btn-primary p-4 rounded-full disabled:opacity-50"
              >
                {isLoading ? (
                  <RefreshCw className="w-6 h-6 animate-spin" />
                ) : isPlaying ? (
                  <Pause className="w-6 h-6" />
                ) : (
                  <Play className="w-6 h-6" />
                )}
              </button>
              
              <div className="text-center">
                <div className="text-sm font-medium text-gray-900">
                  {isLoading ? 'Generating preview...' : audioUrl ? 'Preview ready' : 'Click play to generate preview'}
                </div>
                {audioUrl && (
                  <div className="text-xs text-gray-600 mt-1">
                    Using current voice settings
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Tips */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">💡 Preview Tips</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Try different sample texts to test various scenarios</li>
              <li>• Adjust settings and regenerate to hear the differences</li>
              <li>• Use punctuation and capitalization for natural speech</li>
              <li>• Numbers and special characters are pronounced automatically</li>
            </ul>
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <button
            onClick={() => {
              setPreviewText(sampleTexts[0])
              setAudioUrl(null)
            }}
            className="btn-secondary"
          >
            Reset
          </button>
          
          <div className="flex items-center gap-3">
            <button
              onClick={handleGeneratePreview}
              disabled={!previewText.trim() || isLoading}
              className="btn-primary flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              {isLoading ? 'Generating...' : 'Generate Preview'}
            </button>
            
            <button
              onClick={onClose}
              className="btn-secondary"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VoicePreview
